import { locate } from "./html";

type fn<T extends any[], K> = (...args: T) => K;
type anyFunction = fn<any[], any>;

let counter = new Date().valueOf();
export function uid(): string {
	return "u" + counter++;
}

export function extend(target, source, deep = true) {
	if (source) {
		for (const key in source) {
			const sobj = source[key];
			const tobj = target[key];
			if (sobj === undefined) {
				delete target[key];
			} else if (
				deep &&
				typeof tobj === "object" &&
				!(tobj instanceof Date) &&
				!(tobj instanceof Array)
			) {
				extend(tobj, sobj);
			} else {
				target[key] = sobj;
			}
		}
	}
	return target;
}

interface IOBj {
	[key: string]: any;
}
export function copy(source: IOBj, withoutInner?: boolean): IOBj {
	const result: IOBj = {};
	for (const key in source) {
		if (!withoutInner || !key.startsWith("$")) {
			result[key] = source[key];
		}
	}
	return result;
}

export function naturalSort(arr): any[] {
	return arr.sort((a: any, b: any) => {
		const nn = typeof a === "string" ? a.localeCompare(b) : a - b;
		return nn;
	});
}

export function findIndex<T = any>(arr: T[], predicate: (obj: T) => boolean): number {
	const len = arr.length;
	for (let i = 0; i < len; i++) {
		if (predicate(arr[i])) {
			return i;
		}
	}
	return -1;
}

export function isEqualString(from: string, to: string): boolean {
	from = from.toString();
	to = to.toString();
	if (from.length > to.length) {
		return false;
	}
	for (let i = 0; i < from.length; i++) {
		if (from[i].toLowerCase() !== to[i].toLowerCase()) {
			return false;
		}
	}
	return true;
}

export function singleOuterClick(fn: (e: MouseEvent) => boolean) {
	const click = (e: MouseEvent) => {
		if (fn(e)) {
			document.removeEventListener("click", click);
		}
	};
	document.addEventListener("click", click);
}

export function detectWidgetClick(widgetId: string, cb: (inner: boolean) => void): () => void {
	const click = (e: MouseEvent) => cb(locate(e, "data-dhx-widget-id") === widgetId);
	document.addEventListener("click", click);

	return () => document.removeEventListener("click", click);
}

export function unwrapBox<T>(box: T | T[]): T {
	if (Array.isArray(box)) {
		return box[0];
	}
	return box;
}

export function wrapBox<T>(unboxed: T | T[]): T[] {
	if (Array.isArray(unboxed)) {
		return unboxed;
	}
	return [unboxed];
}

export function isDefined<T>(some: T): boolean {
	return some !== null && some !== undefined;
}

export function range(from: number, to: number): number[] {
	if (from > to) {
		return [];
	}
	const result = [];
	while (from <= to) {
		result.push(from++);
	}
	return result;
}
export function isNumeric(val: any): boolean {
	return !isNaN(val - parseFloat(val));
}

export function downloadFile(data: string, filename: string, mimeType = "text/plain"): void {
	const file = new Blob([data], { type: mimeType });
	if (window.navigator.msSaveOrOpenBlob) {
		// IE10+
		window.navigator.msSaveOrOpenBlob(file, filename);
	} else {
		const a = document.createElement("a");
		const url = URL.createObjectURL(file);

		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		setTimeout(function() {
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}, 0);
	}
}

export function debounce(func: anyFunction, wait: number, immediate?: boolean) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			timeout = null;
			if (!immediate) {
				func.apply(this, args);
			}
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) {
			func.apply(this, args);
		}
	};
}

export function compare(obj1, obj2) {
	for (const p in obj1) {
		if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) {
			return false;
		}

		switch (typeof obj1[p]) {
			case "object":
				if (!compare(obj1[p], obj2[p])) {
					return false;
				}
				break;
			case "function":
				if (
					typeof obj2[p] === "undefined" ||
					(p !== "compare" && obj1[p].toString() !== obj2[p].toString())
				) {
					return false;
				}
				break;
			default:
				if (obj1[p] !== obj2[p]) {
					return false;
				}
		}
	}

	for (const p in obj2) {
		if (typeof obj1[p] === "undefined") {
			return false;
		}
	}
	return true;
}

export const isType = (value: any): string => {
	const regex = /^\[object (\S+?)\]$/;
	const matches = Object.prototype.toString.call(value).match(regex) || [];

	return (matches[1] || "undefined").toLowerCase();
};

export const isEmptyObj = obj => {
	for (const key in obj) {
		return false;
	}
	return true;
};

export const getMaxArrayNymber = (array: number[]): number => {
	if (!array.length) return;

	let maxNumber = -Infinity;
	let index = 0;
	const length = array.length;

	for (index; index < length; index++) {
		if (array[index] > maxNumber) maxNumber = array[index];
	}
	return maxNumber;
};

export const getMinArrayNymber = (array: number[]): number => {
	if (!array.length) return;

	let minNumber = +Infinity;
	let index = 0;
	const length = array.length;

	for (index; index < length; index++) {
		if (array[index] < minNumber) minNumber = array[index];
	}
	return minNumber;
};

export interface IContainerConfig {
	lineHeight?: number;
	font?: string;
}

export const getStringWidth = (value: string, config?: IContainerConfig): number => {
	config = {
		font: "normal 14px Roboto",
		lineHeight: 20,
		...config,
	};

	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (config.font) ctx.font = config.font;

	const width = ctx.measureText(value).width;

	canvas.remove();

	return width;
};

export const rgbToHex = (color: string): string => {
	if (color.substr(0, 1) === "#") {
		return color;
	}
	const digits = /(.*?)rgb[a]?\((\d+), *(\d+), *(\d+),* *([\d+.]*)\)/.exec(color);

	const red = parseInt(digits[2], 10)
		.toString(16)
		.padStart(2, "0");
	const green = parseInt(digits[3], 10)
		.toString(16)
		.padStart(2, "0");
	const blue = parseInt(digits[4], 10)
		.toString(16)
		.padStart(2, "0");

	return `#${red}${green}${blue}`;
}
